import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import CloseIcon from '@material-ui/icons/Close';
import { Grid, IconButton } from '@material-ui/core';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import { statusKeyToDisplay } from 'utility/lookup';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import CustomTable from 'components/CustomTable';
import usePostApprovalData from 'hooks/usePostApprovalData';
import { POST_APPROVAL_DETAIL_FETCH, GET_DOWNLOAD_GENERATE_DOC, GET_SIGNED_DOCUMENT } from 'store/key';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useLocation } from 'react-router';
import CustomLink from 'components/CustomLink';
import { setGlobalLoading } from 'store/global/reducer';

const SLayout = styled.div`
    width: ${(props) => (props.isFullView ? '100%' : '300px')};
    display: flex;
    height: 100%;
    position: relative;
`;

const SHeader = styled.div`
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #dcdcdc;
`;

const SContentLayout = styled.div`
    padding: 0 15px 0 22px;
    position: relative;
    width: 100%;
`;

const SCloseLayout = styled.div`
    position: relative;
`;

const SLeftArrowsLayout = styled.div`
    position: absolute;
    top: 45%;
    /* padding: 5px; */
    cursor: pointer;
    z-index: 10;
`;

const SIdSection = styled.div`
    padding: 20px 0;
    border-bottom: 2px solid #9bc2e5;
`;

const SApplicationInfoSection = styled.div`
    padding: 20px 0;
    border-bottom: 2px solid #9bc2e5;
`;

const SRelatedDocumentSection = styled.div`
    padding: 20px 0;
    border-bottom: 2px solid #9bc2e5;
`;

const SItemText = styled.div`
    font-size: 14px;
    margin-top: 10px;
`;

const LoadingLayout = styled.div`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const SideBar = ({ isFullView, onFullViewShow = () => null, setDisplayItem, displayItem }) => {
    const location = useLocation();
    const folder = location.pathname.split('/post-approval-document/')?.[1];
    const { sideBarColumn } = usePostApprovalData({ folder });
    const dispatch = useDispatch();
    const postApprovalDetail = useSelector((state) => state.postApproval.postApprovalDetail);
    const loadingDetail = useSelector((state) => state.postApproval.loadingDetail);

    const showFullView = (isShow = true) => {
        onFullViewShow(isShow);
    };

    useEffect(() => {
        if (!displayItem) return;
        const type = displayItem.docType;
        const id = displayItem.id;
        dispatch({
            type: POST_APPROVAL_DETAIL_FETCH,
            payload: {
                type: type,
                id: id,
            },
        });
        // eslint-disable-next-line
    }, [displayItem]);

    return (
        <SLayout isFullView={isFullView}>
            {loadingDetail ? (
                <LoadingLayout>
                    <CircularProgress size={60} />
                </LoadingLayout>
            ) : (
                <>
                    <SHeader>
                        <div style={{ position: 'absolute', right: 10, top: 10, zIndex: 10 }}>
                            <IconButton
                                onClick={() => {
                                    isFullView ? onFullViewShow(false) : setDisplayItem(null);
                                }}
                            >
                                <CloseIcon style={{ color: '#808080' }} fontSize="small" />
                            </IconButton>
                        </div>
                        <SCloseLayout>
                            <SLeftArrowsLayout
                                onClick={() => {
                                    showFullView(!isFullView);
                                }}
                            >
                                {isFullView ? (
                                    <KeyboardArrowRightIcon fontSize={'small'} />
                                ) : (
                                    <KeyboardArrowLeftIcon fontSize={'small'} />
                                )}
                            </SLeftArrowsLayout>
                        </SCloseLayout>
                    </SHeader>

                    {postApprovalDetail ? (
                        <SContentLayout>
                            <SIdSection>
                                <TextSubHeader>Post approval document</TextSubHeader>
                                <TextSubHeader style={{ marginTop: 5 }}>{postApprovalDetail.subNo}</TextSubHeader>
                            </SIdSection>
                            <SApplicationInfoSection>
                                <TextSubHeader>Initial Application</TextSubHeader>
                                <SItemText>IRB/REC Reference No.: {postApprovalDetail.refNo}</SItemText>
                                <SItemText>
                                    Scientific Title:
                                    <Grid container>
                                        <CustomLink
                                            onClick={() => {
                                                setGlobalLoading(true);

                                                dispatch({
                                                    type: GET_DOWNLOAD_GENERATE_DOC,
                                                    payload: {
                                                        id: postApprovalDetail.appId,
                                                        type: 'applicationform',
                                                        callback: () => {
                                                            setGlobalLoading(false);
                                                        },
                                                    },
                                                });
                                            }}
                                            isChangeColor
                                            isStopJumping
                                        >
                                            {postApprovalDetail.p1scientificTitle}
                                        </CustomLink>
                                    </Grid>
                                </SItemText>
                                <SItemText>
                                    CI:
                                    {postApprovalDetail.coordinator}
                                </SItemText>
                                <SItemText>
                                    Status:
                                    {statusKeyToDisplay(postApprovalDetail.statusId)}
                                </SItemText>
                            </SApplicationInfoSection>
                            <SRelatedDocumentSection>
                                <TextSubHeader>Other Relevant Post-Approval Documents:</TextSubHeader>
                                {isFullView && (
                                    <CustomTable
                                        style={{ marginTop: 20 }}
                                        rows={postApprovalDetail?.relevantDoc ?? []}
                                        columns={sideBarColumn}
                                    />
                                )}

                                {!isFullView &&
                                    postApprovalDetail?.relevantDoc?.map((item) => {
                                        return (
                                            <SItemText key={item.id}>
                                                {item.isUploaded ? (
                                                    <Grid container>
                                                        <CustomLink
                                                            onClick={() => {
                                                                dispatch({
                                                                    type: GET_SIGNED_DOCUMENT,
                                                                    payload: {
                                                                        docType: item.docType,
                                                                        reportId: item.id,
                                                                    },
                                                                });
                                                            }}
                                                            isChangeColor
                                                            isStopJumping
                                                        >
                                                            {item.documentType}
                                                        </CustomLink>
                                                    </Grid>
                                                ) : (
                                                    item.documentType
                                                )}
                                            </SItemText>
                                        );
                                    })}

                                {!postApprovalDetail?.relevantDoc?.length && (
                                    <div style={{ fontSize: 14, marginTop: 10 }}>No Relevant Documents</div>
                                )}
                            </SRelatedDocumentSection>
                        </SContentLayout>
                    ) : (
                        <div style={{ padding: 10 }}>No Document is Found</div>
                    )}
                </>
            )}
        </SLayout>
    );
};

export default SideBar;
